// Takvim uç noktaları — Blazor karşılığı: Octo.Dal.Client/CalendarSubscriptionClient.cs.
// DAL: Octo.Dal/Controllers/CalendarSubscriptionController.cs (route "api/calendar") +
//      Octo.Dal/Controllers/SystemController.cs (api/system/CalendarEvents*).
// Yollar burada tanımlıdır; shared/api/endpoints.ts başka ajanlarca düzenlendiği için dokunulmadı.

import { apiClient } from '@/shared/api/http/client';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import type {
  CalendarSource,
  CalendarSourceCreate,
  CalendarSyncResult,
  LocalCalendarEvent,
  LocalCalendarEventInput,
  MergedEventsResponse,
} from '../model/calendarTypes';

export const CALENDAR_ENDPOINTS = {
  SOURCES: (userId: string) => `/api/calendar/sources/${userId}`,
  SOURCE_CREATE: '/api/calendar/sources',
  SOURCE_UPDATE: (sourceId: string) => `/api/calendar/sources/${sourceId}`,
  SOURCE_DELETE: (sourceId: string, userId: string) => `/api/calendar/sources/${sourceId}/${userId}`,
  SOURCE_TOGGLE: (sourceId: string) => `/api/calendar/sources/${sourceId}/toggle`,
  SOURCE_SYNC: (sourceId: string) => `/api/calendar/sources/${sourceId}/sync`,
  MERGED_EVENTS: (userId: string) => `/api/calendar/events/${userId}`,
  OAUTH_GOOGLE: (userId: string) => `/api/calendar/oauth/google/authorize/${userId}`,
  OAUTH_OUTLOOK: (userId: string) => `/api/calendar/oauth/outlook/authorize/${userId}`,
  LOCAL_EVENTS: '/api/system/CalendarEvents',
  LOCAL_EVENTS_BY_USER: (userId: string) => `/api/system/CalendarEvents/${userId}`,
  LOCAL_EVENT_BY_ID: (eventId: string) => `/api/system/CalendarEvents/ById/${eventId}`,
  LOCAL_EVENT_UPDATE: '/api/system/CalendarEvents/Update',
  LOCAL_EVENT_DELETE: (eventId: string, userId: string) => `/api/system/CalendarEvents/${eventId}/${userId}`,
} as const;

/** `success:false` gövdeleri (sync sonucu gibi) hata sayılmaz; çağıran kendisi yorumlar. */
const raw = { octo: { expectSuccess: false } } as const;

export const calendarApi = {
  // ── Kaynaklar ──
  getSources: (userId: string): Promise<CalendarSource[]> =>
    apiClient.get<CalendarSource[]>(CALENDAR_ENDPOINTS.SOURCES(userId), raw),

  createSource: (source: CalendarSourceCreate): Promise<CalendarSource> =>
    apiClient.post<CalendarSource>(CALENDAR_ENDPOINTS.SOURCE_CREATE, source),

  updateSource: (sourceId: string, source: CalendarSource): Promise<{ success: boolean }> =>
    apiClient.post<{ success: boolean }>(CALENDAR_ENDPOINTS.SOURCE_UPDATE(sourceId), source),

  deleteSource: (sourceId: string, userId: string): Promise<{ success: boolean }> =>
    apiClient.delete<{ success: boolean }>(CALENDAR_ENDPOINTS.SOURCE_DELETE(sourceId, userId)),

  toggleSource: (sourceId: string): Promise<{ success: boolean; isEnabled: boolean }> =>
    apiClient.post<{ success: boolean; isEnabled: boolean }>(CALENDAR_ENDPOINTS.SOURCE_TOGGLE(sourceId)),

  syncSource: (sourceId: string): Promise<CalendarSyncResult> =>
    apiClient.post<CalendarSyncResult>(CALENDAR_ENDPOINTS.SOURCE_SYNC(sourceId), undefined, raw),

  // ── Birleşik etkinlikler ──
  /** Uç kullanıcı bazlıdır; tarih aralığı parametresi YOKTUR, süzme istemcide yapılır. */
  getMergedEvents: (userId: string): Promise<MergedEventsResponse> =>
    apiClient.get<MergedEventsResponse>(CALENDAR_ENDPOINTS.MERGED_EVENTS(userId), raw),

  // ── Yerel etkinlikler (yalnızca Local kaynak) ──
  getLocalEvents: (userId: string): Promise<LocalCalendarEvent[]> =>
    apiClient.get<LocalCalendarEvent[]>(CALENDAR_ENDPOINTS.LOCAL_EVENTS_BY_USER(userId), raw),

  getLocalEvent: (eventId: string): Promise<LocalCalendarEvent> =>
    apiClient.get<LocalCalendarEvent>(CALENDAR_ENDPOINTS.LOCAL_EVENT_BY_ID(eventId), raw),

  createLocalEvent: (event: LocalCalendarEventInput): Promise<LocalCalendarEvent> =>
    apiClient.post<LocalCalendarEvent>(CALENDAR_ENDPOINTS.LOCAL_EVENTS, event),

  /** PUT api/system/CalendarEvents — gövdede CalendarEventId zorunlu. */
  updateLocalEvent: (event: LocalCalendarEventInput): Promise<{ success: boolean }> =>
    apiClient.put<{ success: boolean }>(CALENDAR_ENDPOINTS.LOCAL_EVENTS, event),

  deleteLocalEvent: (eventId: string, userId: string): Promise<{ success: boolean }> =>
    apiClient.delete<{ success: boolean }>(CALENDAR_ENDPOINTS.LOCAL_EVENT_DELETE(eventId, userId)),
};

/** OAuth yetkilendirme adresi — DAL doğrudan sağlayıcıya yönlendirir, yeni pencerede açılır. */
export function oauthAuthorizeUrl(provider: 'google' | 'outlook', userId: string): string {
  const base = runtimeConfig().dalUrl.replace(/\/+$/, '');
  const path =
    provider === 'google'
      ? CALENDAR_ENDPOINTS.OAUTH_GOOGLE(userId)
      : CALENDAR_ENDPOINTS.OAUTH_OUTLOOK(userId);
  return `${base}${path}`;
}
